import {fillExtrusionUniforms, fillExtrusionPatternUniforms} from './fill_extrusion_program';
import {fillUniforms, fillPatternUniforms, fillOutlineUniforms, fillOutlinePatternUniforms} from './fill_program';
import {lineBlendCompositeUniforms} from './line_blend_composite_program';
import {lineBlendReduceUniforms} from './line_blend_reduce_program';
import {circleUniforms} from './circle_program';
import {collisionUniforms, collisionCircleUniforms, type CollisionDebugDefinesType} from './collision_program';
import {debugUniforms} from './debug_program';
import {clippingMaskUniforms} from './clipping_mask_program';
import {heatmapUniforms, heatmapTextureUniforms} from './heatmap_program';
import {hillshadeUniforms, hillshadePrepareUniforms} from './hillshade_program';
import {lineUniforms, linePatternUniforms} from './line_program';
import {rasterUniforms} from './raster_program';
import {symbolUniforms} from './symbol_program';
import {backgroundUniforms, backgroundPatternUniforms} from './background_program';
import {terrainRasterUniforms} from '../../terrain/terrain_raster_program';
import {skyboxUniforms, skyboxGradientUniforms} from './skybox_program';
import {skyboxCaptureUniforms} from './skybox_capture_program';
import {globeRasterUniforms, atmosphereUniforms} from '../../terrain/globe_raster_program';
import {starsUniforms} from '../../terrain/stars_program';
import {occlusionUniforms} from './occlusion_program';

import type {GlobeDefinesType} from '../../terrain/globe_raster_program';
import type {HeatmapDefinesType} from './heatmap_program';
import type {HillshadeDefinesType} from './hillshade_program';
import type {LineDefinesType} from './line_program';
import type {SymbolDefinesType} from './symbol_program';
import type {RasterParticleDefinesType} from './raster_particle_program';
import type {RasterDefinesType} from './raster_program';
import type {CircleDefinesType} from './circle_program';
import type {ModelDefinesType} from '../../../3d-style/render/program/model_program';
import type {FillDefinesType} from './fill_program';
import type {FillExtrusionDefinesType} from './fill_extrusion_program';
import type {BuildingDefinesType} from '../../../3d-style/render/program/building_program';
import type {ProgramUniformsHDType} from '../../../3d-style/render/program/program_uniforms_hd';
import type {ProgramUniformsStandardType} from '../../../3d-style/render/program/program_uniforms_standard';

export type FogDefinesType = 'FOG';
export type TerrainDepthAccessDefinesType = 'DEPTH_D24' | 'DEPTH_OCCLUSION';

type GlobalDefinesType =
    | 'DEBUG_WIREFRAME'
    | 'FOG'
    | 'GLOBE'
    | 'LIGHTING_3D_ALPHA_EMISSIVENESS'
    | 'LIGHTING_3D_MODE'
    | 'NORMAL_OFFSET'
    | 'OVERDRAW_INSPECTOR'
    | 'RENDER_CUTOFF'
    | 'RENDER_SHADOWS'
    | 'RENDER_TO_TEXTURE'
    | 'TERRAIN_DEM_FLOAT_FORMAT'
    | 'TERRAIN'
    | 'TERRAIN_VERTEX_MORPHING';

export type DynamicDefinesType =
    | GlobalDefinesType
    | CircleDefinesType
    | SymbolDefinesType
    | CollisionDebugDefinesType
    | LineDefinesType
    | FillDefinesType
    | FillExtrusionDefinesType
    | HeatmapDefinesType
    | GlobeDefinesType
    | RasterDefinesType
    | RasterParticleDefinesType
    | FogDefinesType
    | HillshadeDefinesType
    | TerrainDepthAccessDefinesType
    | ModelDefinesType
    | BuildingDefinesType
    | `MAX_UBO_SIZE_VEC4 ${number}u`;

export const programUniforms = {
    fillExtrusion: fillExtrusionUniforms,
    fillExtrusionPattern: fillExtrusionPatternUniforms,
    fill: fillUniforms,
    fillPattern: fillPatternUniforms,
    fillOutline: fillOutlineUniforms,
    fillOutlinePattern: fillOutlinePatternUniforms,
    lineBlendComposite: lineBlendCompositeUniforms,
    lineBlendReduce: lineBlendReduceUniforms,
    circle: circleUniforms,
    collisionBox: collisionUniforms,
    collisionCircle: collisionCircleUniforms,
    debug: debugUniforms,
    clippingMask: clippingMaskUniforms,
    heatmap: heatmapUniforms,
    heatmapTexture: heatmapTextureUniforms,
    hillshade: hillshadeUniforms,
    hillshadePrepare: hillshadePrepareUniforms,
    line: lineUniforms,
    linePattern: linePatternUniforms,
    raster: rasterUniforms,
    symbol: symbolUniforms,
    background: backgroundUniforms,
    backgroundPattern: backgroundPatternUniforms,
    terrainRaster: terrainRasterUniforms,
    skybox: skyboxUniforms,
    skyboxGradient: skyboxGradientUniforms,
    skyboxCapture: skyboxCaptureUniforms,
    globeRaster: globeRasterUniforms,
    globeAtmosphere: atmosphereUniforms,
    stars: starsUniforms,
    occlusion: occlusionUniforms
} as const;

export type ProgramUniformsType = {
    [K in keyof typeof programUniforms]: ReturnType<typeof programUniforms[K]>;
} & ProgramUniformsHDType & ProgramUniformsStandardType;
